import type { ReplayDoc } from "../data/replayApi";
import { ReplayTrend } from "./IntensityTrend";

const INDEX_NAMES: Record<string, string> = {
  CDI: "Core Destructiveness",
  IKE: "Integrated Kinetic Energy",
  FSTI: "Far-Field Tornado",
  IFI: "Inland Flooding",
  SGM: "Synoptic Gradient",
};
const INDEX_ORDER = ["CDI", "IKE", "FSTI", "IFI", "SGM"];

// Saffir-Simpson from vmax in KNOTS (ATCF/HURDAT2 unit).
function ssCategory(vmax: number): string {
  if (vmax >= 137) return "5";
  if (vmax >= 113) return "4";
  if (vmax >= 96) return "3";
  if (vmax >= 83) return "2";
  if (vmax >= 64) return "1";
  if (vmax >= 34) return "TS";
  return "TD";
}

function severityColor(score: number): string {
  if (score <= 3) return "#2ecc71";
  if (score <= 5) return "#f39c12";
  if (score <= 7) return "#e67e22";
  if (score <= 8.5) return "#e74c3c";
  return "#8b0000";
}

interface Props {
  replay: ReplayDoc | null;
  hour: number;
  loading: boolean;
}

export default function ExplorerPanel({ replay, hour, loading }: Props) {
  if (!replay) {
    return (
      <div className="panel empty">
        <div style={{ fontSize: 40 }}>🌀</div>
        <b>Select a storm</b>
        <p className="muted">{loading ? "Loading replay…" : "Pick a storm to scrub its Threat Fingerprint hour-by-hour."}</p>
      </div>
    );
  }

  const f = replay.frames[hour];
  if (!f) return <div className="panel">No data for this hour.</div>;

  const [score, label, color] = f.overall;
  const cat = ssCategory(f.vmax);

  return (
    <div className="panel">
      <div className="live-header">
        <h3 style={{ margin: 0 }}>🌀 {replay.name}</h3>
        <span style={{ color, fontWeight: 700, fontSize: 22 }}>
          {score}<span style={{ fontSize: 13, color }}> {label}</span>
        </span>
      </div>
      <div className="muted sm" style={{ marginTop: 2 }}>
        Cat {cat} · {Math.round(f.vmax)} kt · {Math.round(f.pmin)} mb
      </div>
      <ReplayTrend frames={replay.frames} hour={hour} />

      <div style={{ marginTop: 14 }}>
        {INDEX_ORDER.map((key) => {
          const v = f.idx[key] ?? 0;
          const c = severityColor(v);
          return (
            <div key={key} style={{ marginBottom: 11 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>{INDEX_NAMES[key]}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: c }}>
                  {v}/10
                  <span style={{ fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,0.35)", marginLeft: 4 }}>
                    {f.labels[key] ?? ""}
                  </span>
                </span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 3, height: 5, overflow: "hidden" }}>
                <div style={{ background: c, height: "100%", width: `${Math.min(100, v * 10)}%`, borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="metric" style={{ marginTop: 12 }}>
        <div className="muted xs">Traditional classification</div>
        <span style={{ fontSize: 13 }}>Saffir-Simpson: <b>Category {cat}</b> — says nothing about surge, flooding, or tornado risk.</span>
      </div>
    </div>
  );
}
